import { Box, Button } from '@mui/material';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import { useAppDispatch } from 'store/store';
import { getActiveTicket, getSidebarView } from 'store/ticket-service/selector';
import {
	setActiveHistoryItem,
	setHistoryMode,
	setReportAnIssueMode,
	setSidebarView,
} from 'store/ticket-service/ticketSlice';
import { getActiveSubscription, getAllPaymentMethods } from 'store/user-service/asyncActions';
import { getCurrentSubscription } from 'store/user-service/selector';
import { setCurrentSubscription, setIsOpenUserProfile } from 'store/user-service/userSlice';

import { ROUTES } from 'core/constants/routes';
import { NamespacesList } from 'core/enums/common';
import { useGetUser } from 'core/hooks/useGetUser';

import { CreateTicketModal } from 'components/Ticket/CreateTicketModal';
import { LanguageSelect } from 'components/shared/CustomSelect/LanguageSelect';

import { ReactComponent as Logo } from 'assets/icons/logo-ellogy.svg';
import { ReactComponent as MessageQuestion } from 'assets/icons/message-question.svg';

import { LogoutModal } from '../LogoutModal/LogoutModal';

interface HeaderProps {
	isCreating?: boolean;
}

export const LoginHeader: FC<HeaderProps> = ({ isCreating }) => {
	const { i18n } = useTranslation();
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const dispatch = useAppDispatch();

	const activeTicket = useSelector(getActiveTicket);
	const sidebarView = useSelector(getSidebarView);
	const currentSubscription = useSelector(getCurrentSubscription);

	const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
	const [isShowDraft, setIsShowDraft] = useState(false);
	const { user } = useGetUser();

	const handleGetAllPaymentsMethods = async () => {
		try {
			await dispatch(getAllPaymentMethods({ recordsPerPage: 5 })).unwrap();
		} catch (error) {
			console.error('getAllPaymentMethods error', error);
		}
	};

	const handleGetCurrentSubscriptionLevel = async () => {
		if (user) {
			const activeTariff = await dispatch(getActiveSubscription()).unwrap();

			if (activeTariff) dispatch(setCurrentSubscription(activeTariff));
		}
	};

	const locationObject = useLocation();

	const handleProfileAction = () => {
		dispatch(setReportAnIssueMode(false));
		dispatch(setHistoryMode(false));
		dispatch(setActiveHistoryItem(null));
		dispatch(setIsOpenUserProfile(true));
	};

	const handleCloseCreateTicketModal = () => {
		if (setIsShowDraft) {
			handleProfileAction();
			setIsShowDraft(false);
		}
	};

	const handleCloseLogoutModal = () => {
		setIsLogoutModalOpen(false);
	};

	useEffect(() => {
		if (localStorage.getItem('i18nextLng') !== ('en' && 'ar')) {
			window.localStorage.setItem('i18nextLng', 'en');
		}
		if (i18n.language !== 'en' && i18n.language !== 'ar') {
			i18n.changeLanguage('en');
		}
	}, []);

	useEffect(() => {
		if (user && user.stripeCustomerId && user.stripeCustomerId.length > 0) {
			handleGetAllPaymentsMethods();
		}
	}, [user]);

	useEffect(() => {
		if (!currentSubscription) handleGetCurrentSubscriptionLevel();
	}, [currentSubscription]);

	const isLoginPage = useMemo(
		() =>
			locationObject.pathname !== ROUTES.LOGIN &&
			locationObject.pathname !== ROUTES.SIGNUP &&
			locationObject.pathname !== ROUTES.RESET_PASSWORD &&
			locationObject.pathname !== ROUTES.CREATE_NEW_PASSWORD,
		[locationObject.pathname]
	);

	const renderButton = () => {
		if (locationObject.pathname === ROUTES.LOGIN) {
			return (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: '20px',
					}}
				>
					<LanguageSelect />

					<Button
						component={Link}
						to="/sign-up"
						sx={{
							width: '150px',
							height: '45px',
							border: '1px solid #4786FF',
							borderRadius: '8px',
							textTransform: 'none',
							fontWeight: '700',
						}}
					>
						{commonTranslate('buttons.signUp')}
					</Button>
				</Box>
			);
		}

		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: '20px',
				}}
			>
				<Button
					component={Link}
					to="/sign-in"
					sx={{
						width: '150px',
						height: '45px',
						border: '1px solid #4786FF',
						borderRadius: '8px',
						textTransform: 'none',
						fontWeight: '700',
					}}
				>
					{commonTranslate('buttons.logIn')}
				</Button>
			</Box>
		);
	};

	return (
		<Box
			sx={{
				display: { xs: window.innerWidth < 900 && !sidebarView ? 'none' : 'flex', md: 'flex' },
				justifyContent: 'center',
				borderBottom: '1px solid #E7E8EC',
				background: '#FBFBFB',
				boxShadow: '0px 4px 16px rgba(40, 103, 131, 0.06)',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					padding: { xs: '12px 20px', md: '12px 30px' },
					width: { xs: '90%', md: '100%' },
				}}
			>
				<Box
					sx={{
						display: {
							xs: isLoginPage ? 'flex' : 'none',
							md: 'flex',
						},
						alignItems: 'center',
						gap: '10px',
					}}
				>
					<Logo />
				</Box>

				<Button
					sx={{
						display: {
							xs: isLoginPage ? 'flex' : 'none',
							md: 'none',
						},
						height: '44px',
						padding: '0',
						minWidth: '44px',
						width: '44px',
						borderRadius: '8px',
						textTransform: 'none',
						fontSize: '16px',
						fontWeight: '700',
						gap: '8px',
					}}
					variant="outlined"
					color="primary"
					onClick={() => {
						activeTicket && dispatch(setReportAnIssueMode(true));
						activeTicket && dispatch(setSidebarView(false));
					}}
				>
					<MessageQuestion />
				</Button>

				{renderButton()}
			</Box>
			{isShowDraft && activeTicket && (
				<CreateTicketModal
					handleCloseModal={handleCloseCreateTicketModal}
					ticket={activeTicket}
					userId={user.id}
					isCreating={isCreating}
				/>
			)}
			{isLogoutModalOpen && !isShowDraft && (
				<LogoutModal handleCloseModal={handleCloseLogoutModal} />
			)}
		</Box>
	);
};
